// Detection of external AI-assistant referrals (ChatGPT, Perplexity, Claude,
// Gemini, Copilot ...). Answer engines are becoming a real acquisition
// channel, and they arrive with no UTM tags — only a referrer host.
//
// This is deliberately non-invasive: we read `document.referrer` (already sent
// by the browser), map the host to a coarse channel label, and store it for
// the session so a later lead submission can be attributed honestly. No
// fingerprinting, no cross-site identifiers, and no fabricated attribution —
// when the referrer is empty or unrecognised we record nothing.

const STORAGE_KEY = "sam.airef.v1";

const AI_HOSTS: { match: RegExp; source: string }[] = [
  { match: /(^|\.)chatgpt\.com$/i, source: "chatgpt" },
  { match: /(^|\.)chat\.openai\.com$/i, source: "chatgpt" },
  { match: /(^|\.)openai\.com$/i, source: "openai" },
  { match: /(^|\.)perplexity\.ai$/i, source: "perplexity" },
  { match: /(^|\.)claude\.ai$/i, source: "claude" },
  { match: /(^|\.)gemini\.google\.com$/i, source: "gemini" },
  { match: /(^|\.)copilot\.microsoft\.com$/i, source: "copilot" },
  { match: /(^|\.)bing\.com$/i, source: "" }, // plain search — not an AI referral
  { match: /(^|\.)you\.com$/i, source: "you" },
  { match: /(^|\.)poe\.com$/i, source: "poe" },
];

export type AiReferral = { utm_source: string; utm_medium: "ai_referral" };

function classify(referrer: string): string | null {
  if (!referrer) return null;
  let host = "";
  try {
    host = new URL(referrer).hostname;
  } catch {
    return null;
  }
  for (const h of AI_HOSTS) {
    if (h.match.test(host)) return h.source || null;
  }
  return null;
}

function read(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Capture the AI referrer once per session (first touch wins) and return the
 * resulting channel, or null when the visit did not come from an AI surface.
 */
export function captureAiReferrer(): string | null {
  if (typeof window === "undefined") return null;
  const stored = read();
  if (stored) return stored;
  const source = classify(document.referrer || "");
  if (!source) return null;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, source);
  } catch {
    /* storage disabled — the in-page value below still applies */
  }
  return source;
}

/** Current AI-referral attribution, if any, in UTM-shaped fields. */
export function getAiReferral(): AiReferral | null {
  if (typeof window === "undefined") return null;
  const source = read() ?? classify(document.referrer || "");
  return source ? { utm_source: source, utm_medium: "ai_referral" } : null;
}
